import React, { FC, useState } from 'react';

import { Box, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import {
  ChakraMarkdown,
  CustomTooltip,
  formatNumberWithComma,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { SubControlsDrawer } from 'containers/Compliance/Components/ComplianceTables/ComplianceControlTable/SubControlsDrawer';
import { History } from 'containers/Compliance/Components/History';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';
import { SeverityComponent } from 'containers/Compliance/Components/SeverityComponent';

import { renderPassedChecks } from '../utils';

export const ControlTable: FC = () => {
  const {
    controlStandardRules: standardRules,
    selectedBlueprint,
  } = useSelector(selectCompliance);

  const {
    renderRulesActions,
    isOpenSeverityModal,
    isOpenHistory,
    selectedResource,
  } = useCompliance();

  const dispatch = useDispatch();
  const subControlsDrawer = useDisclosure();
  const [selectedControl, setSelectedControl] = useState<Record<string, any>>(
    {},
  );

  const columns = [
    {
      header: <Box pl={10}>Control</Box>,
      accessor: 'control',
      align: 'left',
      render: ({ value }) => {
        return (
          <WithResourceIcon resourceType={selectedBlueprint?.compliance!}>
            <Box>{value}</Box>
          </WithResourceIcon>
        );
      },
      styles: { width: '150px', cell: { maxWidth: '150px' } },
    },
    {
      header: 'Control Description',
      accessor: 'control_description',
      align: 'left',
      render: ({ value }) => (
        <CustomTooltip
          label={<ChakraMarkdown content={value || '-'} />}
          styles={{ text: { w: 'full' } }}
        >
          <Box whiteSpace="wrap" noOfLines={2} flex={1}>
            {value || '-'}
          </Box>
        </CustomTooltip>
      ),
    },
    {
      header: 'Ariksa Policies',
      accessor: 'rules',
      render: ({ value }) => value.total ?? '-',
      styles: { width: '130px', cell: { maxWidth: '130px' } },
    },
    {
      header: 'Entities',
      accessor: 'check_stats.total_entities',
      render: ({ value }) => formatNumberWithComma(value),
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
    {
      header: 'Exceptions',
      accessor: 'rules',
      render: ({ value }) => {
        if (value?.total && value?.selected) {
          return value?.total - value?.selected;
        } else {
          return '-';
        }
      },
      styles: { width: '130px', cell: { maxWidth: '130px' } },
    },
    {
      header: 'Passed Policies',
      accessor: 'check_stats',
      render: renderPassedChecks,
      align: 'left',
      styles: { width: '220px', cell: { maxWidth: '220px' } },
    },
    {
      header: 'Actions',
      render: renderRulesActions,
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
  ];

  return (
    <>
      <Table
        data={standardRules.data}
        columns={columns}
        cursor="pointer"
        isLoading={standardRules.isLoading}
        onRowClick={row => {
          setSelectedControl(row);
          subControlsDrawer.onOpen();
        }}
        pagination={{
          totalCount: standardRules.page.totalCount,
          pageInfo: standardRules.page.info,
          onChange: pageInfo => {
            dispatch(actions.updateStandardRulesPageInfo(pageInfo));
          },
        }}
        headerClassName="compliance_by_standards_rules"
      />
      {isOpenSeverityModal && <SeverityComponent />}
      {isOpenHistory && (
        <History
          control={selectedResource?.control}
          subControl={selectedResource?.sub_control}
        />
      )}
      {subControlsDrawer.isOpen && (
        <SubControlsDrawer
          control={selectedControl}
          isOpen={subControlsDrawer.isOpen}
          onClose={subControlsDrawer.onClose}
        />
      )}
    </>
  );
};
